import { useTranslations } from '@vocab/react';
import {
  Box,
  ContentBlock,
  Heading,
  IconTick,
  List,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';

import type {
  HirerAccountFragment,
  IntegrationRelationshipType,
  RequestIntegrationRelationshipsSuccess,
} from 'src/App/generated/graphql-types';
import { CopyableText } from 'src/components/CopyableText/CopyableText';
import { SvgImage } from 'src/components/SvgImage/SvgImage';
import ReceivedRequestSvg from 'src/images/received-request.svg';
import translations from 'src/pages/LinkYourSoftware/.vocab';
import type { Partner, RelationshipTypeCode } from 'src/types';
import { useIsAuNz } from 'src/utils/hooks/useContext';

interface Props {
  partner: Partner;
  hirers: HirerAccountFragment[];
  relationshipTypeCodes: IntegrationRelationshipType[];
  requestResult: RequestIntegrationRelationshipsSuccess;
}

export const ReceivedRequest = ({
  partner,
  hirers,
  relationshipTypeCodes,
  requestResult,
}: Props) => {
  const { t } = useTranslations(translations);
  const isAuNz = useIsAuNz();
  const isAutomated = requestResult.relationships.every(
    ({ __typename }) => __typename === 'ApprovedIntegrationRelationship',
  );

  const hirerOidsBySeekId = Object.fromEntries(
    requestResult.relationships.map(({ hirerAccountId, hirerOid }) => [
      hirerAccountId,
      hirerOid,
    ]),
  );

  // The form can get quite long with a lot of hirer accounts.
  window.scrollTo(0, 0);
  return (
    <Box id="lys-received-request">
      <ContentBlock width="small">
        <Stack space="xlarge">
          <Stack space={{ tablet: 'large', mobile: 'xsmall' }}>
            <SvgImage image={ReceivedRequestSvg} />
            <Heading align="center" level="2">
              {t('receivedYourRequest')}
            </Heading>
          </Stack>
          {isAutomated ? (
            <Box id="lys-automated-request-success">
              <Stack space="xlarge">
                <Stack space="gutter">
                  <Text>
                    <Strong>{`You’re accessing these SEEK solutions through ${partner.name}`}</Strong>
                  </Text>
                  <Box
                    background="neutralSoftHover"
                    padding="gutter"
                    borderRadius="large"
                  >
                    <List
                      type="icon"
                      icon={<IconTick tone="positive" />}
                      space="gutter"
                    >
                      {relationshipTypeCodes.map(({ typeCode }) => (
                        <Text key={typeCode}>
                          <Strong>{t(typeCode as RelationshipTypeCode)}</Strong>
                        </Text>
                      ))}
                    </List>
                  </Box>
                  <Text tone="secondary">
                    {`We’ll send you an email to confirm your access and give you more information about setting up ${partner.name}.`}
                  </Text>
                </Stack>

                <Stack space="medium">
                  <Stack space="small">
                    <Text>
                      <Strong>{`Remember to complete the set-up with your connected partner`}</Strong>
                    </Text>
                    <Text>{`Use the information below to set up ${partner.name}:`}</Text>
                  </Stack>
                  <Box
                    background="neutralSoftHover"
                    padding="gutter"
                    paddingBottom="medium"
                    borderRadius="large"
                  >
                    <List space="gutter">
                      {hirers.map(
                        ({ id, billingAccountId, businessName }, index) => (
                          <Stack
                            space="small"
                            key={`integrated-hirer-${index}`}
                          >
                            <Text>
                              <Strong>
                                {`${businessName}${
                                  billingAccountId
                                    ? `: (${parseInt(billingAccountId, 10)})`
                                    : null
                                }`}
                              </Strong>
                            </Text>
                            <CopyableText value={hirerOidsBySeekId[id]}>
                              {`Hirer ID: ${hirerOidsBySeekId[id]}`}
                            </CopyableText>
                          </Stack>
                        ),
                      )}
                    </List>
                  </Box>
                  <Text tone="secondary">
                    If you need help, reach out to our Customer Service team on{' '}
                    <Strong>1300 658 700</Strong>.
                  </Text>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Stack space="medium">
              <Text align="center">{t('thanks')}</Text>
              {isAuNz && (
                <Text align="center">
                  If you need to speak to us urgently, please call customer
                  service on <Strong>1300 658 700</Strong>.
                </Text>
              )}
            </Stack>
          )}
        </Stack>
      </ContentBlock>
    </Box>
  );
};
